import { useState, useEffect, useRef } from 'react';
import { Bot, Send, Trash2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';

export default function AIChatPanel({ groupId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, [groupId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/ai/chat/${groupId}/history`);
      setMessages(res.data);
    } catch { }
    finally { setHistoryLoading(false); }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, created_at: new Date().toISOString() }]);
    setLoading(true);

    // Add empty assistant message for streaming
    setMessages(prev => [...prev, { role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }]);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${baseUrl}/api/ai/chat/${groupId}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            fullText += data;
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: fullText, created_at: new Date().toISOString() };
              return updated;
            });
          }
        }
      }
    } catch (error) {
      // Fallback to non-streaming
      try {
        const res = await api.post(`/ai/chat/${groupId}`, { message: userMsg });
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: res.data.response, created_at: new Date().toISOString() };
          return updated;
        });
      } catch (err) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, an error occurred.', created_at: new Date().toISOString() };
          return updated;
        });
        toast.error(err.response?.data?.detail || 'AI request failed');
      }
    } finally { setLoading(false); }
  };

  const handleClear = async () => {
    try {
      await api.delete(`/ai/chat/${groupId}/history`);
      setMessages([]);
      toast.success('Chat cleared');
    } catch { toast.error('Failed to clear'); }
  };

  const suggestions = [
    "Who has the most fines?",
    "Spending pattern this month?",
    "Who owes the most?",
    "Summary of all expenses",
  ];

  return (
    <Card className="flex flex-col h-[500px] border-2 rounded-2xl overflow-hidden" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between bg-primary/5">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-sm">AI Insights</h3>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={handleClear} className="h-7 w-7 p-0" data-testid="clear-ai-chat">
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {historyLoading ? (
          <p className="text-center text-muted-foreground text-sm">Loading...</p>
        ) : messages.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">Ask me about expenses, balances, patterns...</p>
            <div className="grid grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => { setInput(s); }}
                  className="text-xs p-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 text-left transition-colors"
                  data-testid={`ai-suggestion-${s.slice(0, 10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[92%] p-2.5 rounded-xl text-sm ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap'
                  : 'bg-muted rounded-bl-sm ai-markdown'
              }`}>
                {msg.role === 'user' ? msg.content : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => (
                        <a
                          href="#"
                          onClick={async (e) => {
                            e.preventDefault();
                            if (!href) return;
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(href, { headers: { Authorization: `Bearer ${token}` } });
                              if (!res.ok) throw new Error('Download failed');
                              const blob = await res.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const cd = res.headers.get('Content-Disposition') || '';
                              const m = cd.match(/filename="?([^";]+)"?/);
                              a.download = m ? m[1] : 'download';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Downloaded');
                            } catch (err) {
                              toast.error('Download failed: ' + err.message);
                            }
                          }}
                          className="text-primary font-semibold underline hover:text-primary/80"
                          data-testid="ai-download-link"
                        >
                          {children}
                        </a>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-2 -mx-1">
                          <table className="min-w-full text-xs border-collapse">{children}</table>
                        </div>
                      ),
                      thead: ({ children }) => <thead className="bg-primary/10">{children}</thead>,
                      th: ({ children }) => <th className="px-2 py-1 text-left font-semibold border-b border-border">{children}</th>,
                      td: ({ children }) => <td className="px-2 py-1 border-b border-border/50">{children}</td>,
                      code: ({ children }) => <code className="px-1 py-0.5 rounded bg-background/60 text-xs">{children}</code>,
                      ul: ({ children }) => <ul className="list-disc ml-4 my-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal ml-4 my-1">{children}</ol>,
                      p: ({ children }) => <p className="my-1 leading-snug">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted p-2.5 rounded-xl rounded-bl-sm">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-border flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about expenses..."
          disabled={loading}
          data-testid="ai-chat-input"
          className="h-10 rounded-xl border-2"
        />
        <Button type="submit" disabled={loading || !input.trim()} data-testid="ai-chat-send" className="h-10 w-10 rounded-xl p-0">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
}
